import { errorMessageFromResponse } from "../../../apiError";
import { buildUrl, defaultFetchHeaders, defaultReadHeaders, fetchJson } from "../../../utils";
import { getTaskIdentityKey } from "../../taskContext";
import {
  Inventory,
  InventoryVan,
  InventoryItem,
  InventoryMovement,
  InventoryMovementSource,
  InventoryMovementType,
  MachineInventoryItem,
  MachineInventoryMode,
  Product,
} from "../../types";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const inventoryBase = `${backend}/inventory`;
const productBase = `${backend}/product`;
const machineInventoryBase = `${inventoryBase}/machine/item`;
const inventoryItemBase = `${inventoryBase}/item`;

const jsonHeaders = async () => {
  const identity = getTaskIdentityKey();
  const headers = await defaultFetchHeaders("application/json", defaultReadHeaders());
  if (identity !== getTaskIdentityKey()) throw new Error("Your account changed. Reopen inventory before saving.");
  return { ...headers, "Content-Type": "application/json" };
};

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const numberOrZero = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export type ProductPayload = {
  name: string;
  brand?: string | null;
  code?: string | null;
  barcode?: string | null;
  category?: string | null;
  size?: string | null;
  unitCost?: number | null;
  defaultSellPrice?: number | null;
  unitsPerCase?: number | null;
  isActive?: boolean;
  pricePropagationMode?: ProductPricePropagationMode;
};

export type ProductPricePropagationMode =
  | "default_only"
  | "matching_previous_default"
  | "all_machine_items";

export type MachineInventoryItemPayload = {
  productId: string;
  mode: MachineInventoryMode;
  selectionCode?: string | null;
  capacity: number;
  targetStock?: number;
  currentStock?: number;
  sellPrice?: number | null;
  isActive?: boolean;
};

export type MachineInventoryStockPayload = {
  movementType: InventoryMovementType;
  source?: InventoryMovementSource;
  quantityDelta?: number;
  stockAfter?: number;
  notes?: string | null;
  taskId?: string | null;
};

export type InventoryItemPayload = {
  productId: string;
  capacity: number;
  targetStock?: number;
  thresholdPercent?: number | null;
  currentStock: number;
  isActive?: boolean;
};

export type InventoryItemStockPayload = MachineInventoryStockPayload;

export type InventoryItemBundle = {
  item: InventoryItem;
  product?: Product;
};

export type MachineInventoryItemBundle = {
  item: MachineInventoryItem;
  product?: Product;
};

export type MachineInventoryItemsBundle = {
  items: MachineInventoryItem[];
  products: Product[];
};

export const normalizeProduct = (product: any): Product => ({
  id: String(product?.id || ""),
  name: product?.name || product?.title || "",
  brand: product?.brand ?? null,
  code: product?.code ?? null,
  barcode: product?.barcode ?? null,
  category: product?.category ?? null,
  size: product?.size ?? null,
  unitCost: numberOrNull(product?.unitCost ?? product?.unit_cost),
  defaultSellPrice: numberOrNull(product?.defaultSellPrice ?? product?.default_sell_price),
  unitsPerCase: numberOrNull(product?.unitsPerCase ?? product?.units_per_case),
  isActive: product?.isActive ?? product?.is_active ?? true,
  imgUrl: product?.imgUrl ?? product?.img_url ?? null,
});

const normalizeInventoryItemResponse = (item: any, inventoryId = ""): InventoryItem => ({
  id: String(item?.id || ""),
  inventoryId: String(item?.inventoryId ?? item?.inventory_id ?? inventoryId),
  productId: String(item?.productId ?? item?.product_id ?? item?.product?.id ?? ""),
  capacity: numberOrZero(item?.capacity),
  targetStock: numberOrZero(item?.targetStock ?? item?.target_stock ?? item?.capacity),
  thresholdPercent: numberOrNull(item?.thresholdPercent ?? item?.threshold_percent),
  currentStock: numberOrZero(item?.currentStock ?? item?.current_stock),
  isActive: item?.isActive ?? item?.is_active ?? true,
});

const normalizeInventoryItemBundle = (
  item: any,
  inventoryId = "",
): InventoryItemBundle => {
  const product = item?.product ? normalizeProduct(item.product) : undefined;
  return {
    item: normalizeInventoryItemResponse(item, inventoryId),
    product,
  };
};

const normalizeInventoryResponse = (inventory: any): Inventory => ({
  id: String(inventory?.id || ""),
  title: String(inventory?.title || ""),
});

const normalizeMachineInventoryItemBundle = (item: any): MachineInventoryItemBundle => {
  const product = item?.product ? normalizeProduct(item.product) : undefined;
  return {
    item: {
      id: String(item?.id || ""),
      machineId: String(item?.machineId ?? item?.machine_id ?? item?.machine ?? ""),
      productId: String(item?.productId ?? item?.product_id ?? product?.id ?? ""),
      mode: (item?.mode || "selection") as MachineInventoryMode,
      selectionCode: item?.selectionCode ?? item?.selection_code ?? null,
      capacity: numberOrZero(item?.capacity),
      targetStock: numberOrZero(item?.targetStock ?? item?.target_stock ?? item?.capacity),
      currentStock: numberOrZero(item?.currentStock ?? item?.current_stock),
      sellPrice: numberOrNull(item?.sellPrice ?? item?.sell_price),
      isActive: item?.isActive ?? item?.is_active ?? true,
    },
    product,
  };
};

const normalizeMovement = (movement: any): InventoryMovement => ({
  id: String(movement?.id || ""),
  machineInventoryItem:
    movement?.machineInventoryItem ?? movement?.machine_inventory_item ?? null,
  inventoryItem: movement?.inventoryItem ?? movement?.inventory_item ?? null,
  movementType: (movement?.movementType ?? movement?.movement_type ?? "count") as InventoryMovementType,
  source: (movement?.source || "manual") as InventoryMovementSource,
  quantityDelta: numberOrZero(movement?.quantityDelta ?? movement?.quantity_delta),
  refilledQuantity: numberOrNull(movement?.refilledQuantity ?? movement?.refilled_quantity),
  grossAmount: numberOrNull(movement?.grossAmount ?? movement?.gross_amount),
  stockBefore: numberOrZero(movement?.stockBefore ?? movement?.stock_before),
  stockAfter: numberOrZero(movement?.stockAfter ?? movement?.stock_after),
  notes: movement?.notes ?? null,
  taskId: movement?.taskId ?? movement?.task_id ?? null,
  transferId: movement?.transferId ?? movement?.transfer_id ?? null,
  createdAt: movement?.createdAt ?? movement?.created_at ?? null,
});

export async function fetchProducts(): Promise<Product[]> {
  const list = await fetchJson<any[]>(
    `${productBase}/all`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch products",
  );
  return (Array.isArray(list) ? list : []).map(normalizeProduct);
}

export async function createProduct(payload: ProductPayload): Promise<Product> {
  const product = await fetchJson<any>(
    `${productBase}/create`,
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify(payload),
    },
    "Failed to create product",
  );
  return normalizeProduct(product);
}

export async function editProduct(
  id: string,
  payload: Partial<ProductPayload>,
): Promise<Product> {
  const product = await fetchJson<any>(
    buildUrl(`${productBase}/edit`, { id }),
    {
      method: "PUT",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify(payload),
    },
    "Failed to update product",
  );
  return normalizeProduct(product);
}

export async function fetchInventories(): Promise<{
  inventories: Inventory[];
  inventoryItems: InventoryItem[];
  products: Product[];
}> {
  const list = await fetchJson<any[]>(
    `${inventoryBase}/all`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch inventories",
  );
  const inventories: Inventory[] = [];
  const inventoryItems: InventoryItem[] = [];
  const productsById = new Map<string, Product>();
  (Array.isArray(list) ? list : []).forEach((inventory) => {
    const normalizedInventory = normalizeInventoryResponse(inventory);
    inventories.push(normalizedInventory);
    (inventory?.items || []).forEach((item: any) => {
      const product = item?.product ? normalizeProduct(item.product) : null;
      if (product?.id) productsById.set(product.id, product);
      inventoryItems.push(normalizeInventoryItemResponse(item, normalizedInventory.id));
    });
  });
  return {
    inventories,
    inventoryItems,
    products: Array.from(productsById.values()),
  };
}

export async function createInventoryItem(
  inventoryId: string,
  payload: InventoryItemPayload,
): Promise<InventoryItemBundle> {
  const item = await fetchJson<any>(
    buildUrl(`${inventoryItemBase}/add`, { inventory_id: inventoryId }),
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify(payload),
    },
    "Failed to create warehouse stock",
  );
  return normalizeInventoryItemBundle(item, inventoryId);
}

export async function editInventoryItem(
  id: string,
  payload: Partial<Omit<InventoryItemPayload, "productId">>,
): Promise<void> {
  const res = await fetch(buildUrl(`${inventoryItemBase}/edit`, { id }), {
    method: "PUT",
    credentials: "include",
    headers: await jsonHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await errorMessageFromResponse(res, "Failed to update warehouse stock"));
  }
}

export async function deleteInventoryItem(id: string): Promise<void> {
  await fetchJson(
    buildUrl(`${inventoryItemBase}/delete`, { id }),
    {
      method: "DELETE",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to delete warehouse stock",
  );
}

export async function updateInventoryItemStock(
  id: string,
  payload: InventoryItemStockPayload,
): Promise<InventoryItemBundle> {
  const item = await fetchJson<any>(
    buildUrl(`${inventoryItemBase}/stock`, { id }),
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify({
        source: "manual",
        ...payload,
      }),
    },
    "Failed to update warehouse stock",
  );
  return normalizeInventoryItemBundle(item);
}

export async function fetchMachineInventoryItems(machineId: string): Promise<MachineInventoryItemsBundle> {
  const list = await fetchJson<any[]>(
    buildUrl(`${machineInventoryBase}/all`, { machine_id: machineId }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch machine inventory",
  );
  const productsById = new Map<string, Product>();
  const items = (Array.isArray(list) ? list : []).map((entry) => {
    const bundle = normalizeMachineInventoryItemBundle(entry);
    if (bundle.product?.id) productsById.set(bundle.product.id, bundle.product);
    return bundle.item;
  });
  return {
    items,
    products: Array.from(productsById.values()),
  };
}

export async function fetchAllMachineInventoryItems(): Promise<MachineInventoryItemsBundle> {
  const list = await fetchJson<any[]>(
    `${machineInventoryBase}/all-org`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch machine inventory",
  );
  const productsById = new Map<string, Product>();
  const items = (Array.isArray(list) ? list : []).map((entry) => {
    const bundle = normalizeMachineInventoryItemBundle(entry);
    if (bundle.product?.id) productsById.set(bundle.product.id, bundle.product);
    return bundle.item;
  });
  return {
    items,
    products: Array.from(productsById.values()),
  };
}

export async function createMachineInventoryItem(
  machineId: string,
  payload: MachineInventoryItemPayload,
): Promise<MachineInventoryItemBundle> {
  const item = await fetchJson<any>(
    buildUrl(`${machineInventoryBase}/create`, { machine_id: machineId }),
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify(payload),
    },
    "Failed to create inventory item",
  );
  return normalizeMachineInventoryItemBundle(item);
}

export async function editMachineInventoryItem(
  id: string,
  payload: Partial<MachineInventoryItemPayload>,
): Promise<void> {
  const res = await fetch(buildUrl(`${machineInventoryBase}/edit`, { id }), {
    method: "PUT",
    credentials: "include",
    headers: await jsonHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await errorMessageFromResponse(res, "Failed to update inventory item"));
  }
}

export async function deleteMachineInventoryItem(id: string): Promise<void> {
  await fetchJson(
    buildUrl(`${machineInventoryBase}/delete`, { id }),
    {
      method: "DELETE",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to delete inventory item",
  );
}

export async function updateMachineInventoryStock(
  id: string,
  payload: MachineInventoryStockPayload,
): Promise<MachineInventoryItemBundle> {
  const item = await fetchJson<any>(
    buildUrl(`${machineInventoryBase}/stock`, { id }),
    {
      method: "POST",
      credentials: "include",
      headers: await jsonHeaders(),
      body: JSON.stringify({
        source: "manual",
        ...payload,
      }),
    },
    "Failed to update stock",
  );
  return normalizeMachineInventoryItemBundle(item);
}

export async function fetchMachineInventoryMovements(itemId: string): Promise<InventoryMovement[]> {
  const list = await fetchJson<any[]>(
    buildUrl(`${inventoryBase}/movement/item/all`, { item_id: itemId, item_type: "machine" }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch inventory movement history",
  );
  return (Array.isArray(list) ? list : []).map(normalizeMovement);
}

export async function fetchMachineInventoryMovementsForMachine(machineId: string): Promise<InventoryMovement[]> {
  const list = await fetchJson<any[]>(
    buildUrl(`${inventoryBase}/movement/owner/all`, { owner_id: machineId, owner_type: "machine" }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch inventory movement history",
  );
  return (Array.isArray(list) ? list : []).map(normalizeMovement);
}

export async function fetchAllInventoryMovements(): Promise<InventoryMovement[]> {
  const list = await fetchJson<any[]>(
    `${inventoryBase}/movement/all-org`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch inventory movement history",
  );
  return (Array.isArray(list) ? list : []).map(normalizeMovement);
}

export function fetchInventoryVans(signal?: AbortSignal): Promise<InventoryVan[]> {
  return fetchJson(`${inventoryBase}/van/all`, { credentials: "include", headers: defaultReadHeaders(), signal }, "Failed to load vans");
}

export type InventoryTransferInput = {
  id: string;
  sourceInventoryId: string;
  destinationInventoryId: string;
  createMissingItems: boolean;
  items: { sourceItemId: string; destinationItemId: string; quantity: number }[];
  notes: string;
};

export async function transferInventoryStock(data: InventoryTransferInput): Promise<void> {
  await fetchJson(`${inventoryBase}/transfer`, { method: "POST", credentials: "include", headers: await jsonHeaders(),
    body: JSON.stringify(data) }, "Failed to transfer stock");
}

export async function saveInventoryVan(data: Pick<InventoryVan, "name" | "isActive"> & { id?: string; creationId?: string }): Promise<InventoryVan> {
  return fetchJson(data.id ? `${inventoryBase}/van/${encodeURIComponent(data.id)}` : `${inventoryBase}/van`,
    { method: data.id ? "PUT" : "POST", credentials: "include", headers: await jsonHeaders(),
      body: JSON.stringify({ ...(!data.id && data.creationId ? { id: data.creationId } : {}), name: data.name, isActive: data.isActive }) }, "Failed to save van");
}

export async function assignInventoryVan(userId: string, vanId: string | null, expectedVanId?: string): Promise<void> {
  await fetchJson(`${inventoryBase}/van-assignment`, { method: "PUT", credentials: "include", headers: await jsonHeaders(),
    body: JSON.stringify({ userId, vanId, ...(expectedVanId ? { expectedVanId } : {}) }) }, "Failed to assign van");
}

export async function fetchInventoryVanMovements(vanId: string, offset = 0, signal?: AbortSignal): Promise<InventoryMovement[]> {
  const movements = await fetchJson<unknown[]>(buildUrl(`${inventoryBase}/van/${encodeURIComponent(vanId)}/movements`, { offset, limit: 50 }),
    { credentials: "include", headers: defaultReadHeaders(), signal }, "Failed to load van history");
  return movements.map(normalizeMovement);
}
